import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';

const viteEnv = (import.meta as { env?: Record<string, string | undefined> }).env;
const apiBase = viteEnv?.['VITE_API_BASE_URL'] ?? 'http://localhost:8080';

export const axiosInstance = axios.create({
  baseURL: apiBase,
});

// Token provider — set by App.tsx to Clerk's getToken() for auto-refresh.
// Falls back to the cached token set by setAuthToken().
let _tokenProvider: (() => Promise<string | null>) | null = null;
let _cachedToken: string | null = null;

export function setTokenProvider(provider: () => Promise<string | null>) {
  _tokenProvider = provider;
}

// Kept for backward-compat (used by setAuthToken(null) on sign-out)
export function setAuthToken(token: string | null) {
  _cachedToken = token;
  if (!token) {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
}

// Active claim-mode "lens" — set by the navbar toggle. Sent on every request as X-Claim-Mode so
// the backend computes R&D figures under the chosen methodology (locked claims ignore it).
let _claimMode: string | null = null;
export function setClaimModeHeader(mode: string | null) {
  _claimMode = mode;
}

// Active act-as token — set by the shell's Act-as menu. Sent as X-Act-As on every request so the
// backend clamps req.user.role to the acted role. sessionStorage-backed (per-tab) — see shell.tsx.
// Initialised SYNCHRONOUSLY from sessionStorage at module load so that on a full page reload the
// very first request already carries the header — otherwise page queries can fire (unclamped) in
// the window before the shell's boot effect calls setActAsToken, and a clamped page rendered from
// unclamped/denied data can crash. (The shell still rehydrates + owns enter/exit.)
function readStoredActAsToken(): string | null {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('edison-act-as-token') : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string };
    return typeof parsed.token === 'string' && parsed.token ? parsed.token : null;
  } catch {
    return null;
  }
}
let _actAsToken: string | null = readStoredActAsToken();
export function setActAsToken(token: string | null) { _actAsToken = token; }
export function getActAsToken(): string | null { return _actAsToken; }

// Fail-closed: if the backend rejects the act-as token, end the mode locally rather than silently
// running as the real admin. The shell registers onActAsEnded to clear sessionStorage + query cache
// + show a toast.
let _onActAsEnded: (() => void) | null = null;
export function setOnActAsEnded(cb: (() => void) | null) { _onActAsEnded = cb; }

// Per-request interceptor — always fetches a fresh token via Clerk.
axiosInstance.interceptors.request.use(async (config) => {
  const token = _tokenProvider ? await _tokenProvider() : _cachedToken;
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  } else {
    delete config.headers['Authorization'];
  }
  if (_claimMode) {
    config.headers['X-Claim-Mode'] = _claimMode;
  }
  if (_actAsToken) {
    config.headers['X-Act-As'] = _actAsToken;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (r) => r,
  (error) => {
    const code = error?.response?.data?.error?.code;
    if (code === 'ACT_AS_INVALID' || code === 'ACT_AS_EXPIRED') {
      _actAsToken = null;
      if (_onActAsEnded) _onActAsEnded();
    }
    return Promise.reject(error);
  },
);

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> =>
  axiosInstance.request<T>(config).then((res) => res.data);

// Reviewer (grant-mode) portal auth — set by the authenticated reviewer portal (Group B) to a
// Clerk getToken() closure + the currently-selected claim id. Unlike the anonymous
// X-Access-Token flow (a static, long-lived string), the Clerk bearer token is short-lived, so
// it is re-fetched on every request rather than cached, mirroring the setTokenProvider pattern
// used by the main app's axiosInstance above.
let _reviewerTokenProvider: (() => Promise<string | null>) | null = null;
let _reviewerClaimId: string | null = null;

export function setReviewerTokenProvider(provider: (() => Promise<string | null>) | null) {
  _reviewerTokenProvider = provider;
}

export function setReviewerClaimId(claimId: string | null) {
  _reviewerClaimId = claimId;
}

export type PortalInstanceMode = 'token' | 'grant';

export interface PortalInstanceOptions {
  mode?: PortalInstanceMode;
}

// Resolves the base path + headers a portal request should use for the given mode, without
// constructing a full axios instance. Shared by makePortalInstance and by pages (e.g.
// portal-eligibility) that use raw fetch() instead of the axios wrapper below, so both transports
// agree on exactly how anonymous-token vs. authenticated-reviewer requests are built.
export async function getPortalRequestInit(
  token: string,
  mode: PortalInstanceMode = 'token',
): Promise<{ base: string; headers: Record<string, string> }> {
  if (mode === 'grant') {
    const freshToken = _reviewerTokenProvider ? await _reviewerTokenProvider() : null;
    const headers: Record<string, string> = {};
    if (freshToken) headers['Authorization'] = `Bearer ${freshToken}`;
    if (_reviewerClaimId) headers['X-Claim-Id'] = _reviewerClaimId;
    return { base: '/api/reviewer', headers };
  }
  return { base: '/api/portal', headers: { 'X-Access-Token': token } };
}

// Creates a portal client. Two modes:
//  - 'token' (default — UNCHANGED behavior): anonymous access-token portal. Static
//    X-Access-Token header, requests hit /api/portal/*.
//  - 'grant': authenticated external-reviewer portal (Group B). Authorization: Bearer <clerk
//    token> (refreshed per-request via setReviewerTokenProvider) + X-Claim-Id header (via
//    setReviewerClaimId). Request URLs are rewritten from /api/portal/* to /api/reviewer/* so the
//    SAME page components work unmodified in both modes.
// Methods resolve with response.data directly (same convention as customInstance)
// so callers can do: portal.get<ApiEnvelope>(url).then(res => res.data.items)
export function makePortalInstance(token: string, options?: PortalInstanceOptions) {
  const mode = options?.mode ?? 'token';

  if (mode === 'grant') {
    const instance = axios.create({ baseURL: apiBase });
    instance.interceptors.request.use(async (config) => {
      const freshToken = _reviewerTokenProvider ? await _reviewerTokenProvider() : null;
      if (freshToken) {
        config.headers['Authorization'] = `Bearer ${freshToken}`;
      }
      if (_reviewerClaimId) {
        config.headers['X-Claim-Id'] = _reviewerClaimId;
      }
      if (typeof config.url === 'string' && config.url.startsWith('/api/portal')) {
        config.url = `/api/reviewer${config.url.slice('/api/portal'.length)}`;
      }
      return config;
    });
    return {
      get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
        return instance.get<T>(url, { params }).then((res) => res.data);
      },
      post<T = unknown>(url: string, data?: unknown): Promise<T> {
        return instance.post<T>(url, data).then((res) => res.data);
      },
      patch<T = unknown>(url: string, data?: unknown): Promise<T> {
        return instance.patch<T>(url, data).then((res) => res.data);
      },
      delete<T = unknown>(url: string): Promise<T> {
        return instance.delete<T>(url).then((res) => res.data);
      },
    };
  }

  const instance = axios.create({
    baseURL: apiBase,
    headers: { 'X-Access-Token': token },
  });
  return {
    // Second arg accepts any object — query params are passed in `params`, but
    // portal-expenditure passes { hourlyRate } directly so we forward as params.
    get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
      return instance.get<T>(url, { params }).then((res) => res.data);
    },
    post<T = unknown>(url: string, data?: unknown): Promise<T> {
      return instance.post<T>(url, data).then((res) => res.data);
    },
    patch<T = unknown>(url: string, data?: unknown): Promise<T> {
      return instance.patch<T>(url, data).then((res) => res.data);
    },
    delete<T = unknown>(url: string): Promise<T> {
      return instance.delete<T>(url).then((res) => res.data);
    },
  };
}
